import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalHeader } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canCurrentUserUseProductCapture } from "../../helpers/productCaptureEnabled";
import {
  CaptureKind,
  createProductCaptureLink,
  listProductCaptureLinks,
  revokeProductCaptureLink,
} from "../../services/productCaptureApi";
import { CaptureLinkRow, createCaptureLinkBody, linksForProcess } from "./captureLinkRows";

type RequestCapturePanelProps = {
  productId?: string;
  processTitle: string;
  processIndex?: number;
  productProcessId?: string;
};

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const growTextarea = (el: HTMLElement | null) => {
  if (!el) return;
  el.style.height = "0px";
  el.style.height = `${Math.max(el.scrollHeight, 32)}px`;
};

const asProductRef = (value: any): string => {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "object") {
    const nested = value.$oid || value.oid || value._id || value.id || value.productUniqueId;
    if (typeof nested === "string") {
      return nested.trim();
    }
  }
  return String(value).trim();
};

const statusLabel = (status?: string) => {
  if (status === "OPEN") return "Active";
  if (status === "REVOKED") return "Inactive · revoked";
  if (status === "EXPIRED") return "Inactive · expired";
  if (status === "USED_UP") return "Inactive · used up";
  return status || "Inactive";
};

const RequestCapturePanel = ({
  productId,
  processTitle,
  processIndex,
  productProcessId,
}: RequestCapturePanelProps) => {
  const [instruction, setInstruction] = useState("");
  const [sendToPhone, setSendToPhone] = useState("");
  const whatsAppOn = process.env.REACT_APP_WHATSAPP_ENABLED === "true";
  const [hours, setHours] = useState(8);
  const [photo, setPhoto] = useState(true);
  const [video, setVideo] = useState(true);
  const [lastUrl, setLastUrl] = useState("");
  const [links, setLinks] = useState<CaptureLinkRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openList, setOpenList] = useState(false);
  const instructionRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const enabled = canCurrentUserUseProductCapture();
  const title = String(processTitle || "").trim();
  const savedProductId = asProductRef(productId);
  const savedProcessId = String(productProcessId || "").trim();

  const loadLinks = () => {
    if (!enabled || !savedProductId || (!title && !savedProcessId)) return;
    listProductCaptureLinks(savedProductId, title, savedProcessId || undefined)
      .then((res: any) => {
        if (res?.success && Array.isArray(res.data)) {
          setLinks(res.data);
        }
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!openForm && !openList) return;
    loadLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openForm, openList, savedProductId, savedProcessId, enabled, title]);

  useEffect(() => {
    if (openForm) {
      growTextarea(instructionRef.current);
    }
  }, [openForm, instruction]);

  if (!enabled) {
    return null;
  }

  const forThisProcess = linksForProcess(links, savedProcessId, title);

  const kinds = (): CaptureKind[] => {
    const out: CaptureKind[] = [];
    if (photo) out.push("PHOTO");
    if (video) out.push("VIDEO");
    return out.length ? out : ["PHOTO", "VIDEO"];
  };

  const fullUrl = (row: CaptureLinkRow, token?: string) => {
    const raw = String(token || "").trim();
    const path = row.path || (raw ? `/capture/${encodeURIComponent(raw)}` : "");
    return `${window.location.origin}${path}`;
  };

  const notifySaveProductFirst = () => {
    notifications({
      title: "Save product first",
      message: "Save this product first. Then open Edit product and generate a link.",
      type: "warning",
      duration: 5000,
    });
  };

  const notifyCopy = async (url: string) => {
    const ok = await copyText(url);
    notifications({
      title: ok ? "Success" : "Error",
      message: ok ? "Link copied." : "Could not copy.",
      type: ok ? "success" : "danger",
    });
  };

  const openCapture = () => {
    if (!title) return;
    if (!savedProductId) {
      notifySaveProductFirst();
      return;
    }
    setOpenForm(true);
  };

  const generate = async () => {
    if (!savedProductId) {
      notifySaveProductFirst();
      return;
    }
    if (!title) {
      notifications({ title: "Error", message: "Choose a process name first.", type: "danger" });
      return;
    }
    setBusy(true);
    try {
      const res: any = await createProductCaptureLink(
        createCaptureLinkBody({
          productId: savedProductId,
          processTitle: title,
          processIndex,
          productProcessId: savedProcessId,
          instruction,
          allowedKinds: kinds(),
          expiryHours: hours,
          recipientPhone: whatsAppOn ? sendToPhone : undefined,
        })
      );
      if (!res?.success || !res?.data?.token) {
        const message = String(res?.message || "Could not create capture link.");
        notifications({
          title: message.includes("Save Draft") ? "Save Draft first" : "Error",
          message,
          type: message.includes("Save Draft") ? "warning" : "danger",
        });
        return;
      }
      const url = fullUrl(res.data, res.data.token);
      setLastUrl(url);
      await copyText(url);
      notifications({
        title: "Success",
        message: res.data.whatsAppSent
          ? "Capture link created, copied and sent on WhatsApp."
          : "Capture link created and copied.",
        type: "success",
      });
      loadLinks();
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    try {
      const res: any = await revokeProductCaptureLink(id);
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not revoke.", type: "danger" });
        return;
      }
      loadLinks();
    } finally {
      setBusy(false);
    }
  };

  const shareWhatsApp = (url: string) => {
    const text = encodeURIComponent(
      `Please capture ${title} (up to 4 live photos or videos). Live camera only, then Send. Keep the page open until it says saved. ${url}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="hkp-request-capture hkp-request-capture--trigger">
      <Button
        type="button"
        color="primary"
        outline
        size="sm"
        className="hkp-request-capture__open"
        disabled={!title}
        onClick={openCapture}
      >
        Create capture link
      </Button>
      <Modal
        isOpen={openForm}
        toggle={() => setOpenForm(false)}
        centered
        className="hkp-request-capture-modal"
        modalClassName="hkp-request-capture-modal-wrap"
        backdropClassName="hkp-request-capture-modal-backdrop"
      >
        <ModalHeader toggle={() => setOpenForm(false)}>Ask to capture · {title || "this step"}</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-2">
            The artisan opens the link and shoots live from the loom — up to 4 photos or videos, no gallery.
          </p>
          <label className="hkp-request-capture__label">What to capture</label>
          <Input
            type="textarea"
            rows={1}
            innerRef={instructionRef}
            className="hkp-request-capture__instruction mb-2"
            placeholder="e.g. Close-up of the jangla on the loom, then the full width"
            value={instruction}
            onChange={(e) => {
              setInstruction(e.target.value);
              growTextarea(e.target);
            }}
          />
          {whatsAppOn ? (
            <label className="hkp-request-capture__label">WhatsApp number (optional)</label>
          ) : null}
          {whatsAppOn ? (
            <Input
              type="tel"
              inputMode="tel"
              className="mb-2"
              placeholder="Send on WhatsApp to (optional mobile number)"
              aria-label="Send on WhatsApp to"
              value={sendToPhone}
              onChange={(e) => setSendToPhone(e.target.value)}
            />
          ) : null}
          <div className="hkp-request-capture__fields">
            <label className={`hkp-request-capture__chip${photo ? " is-on" : ""}`}>
              <input type="checkbox" checked={photo} onChange={(e) => setPhoto(e.target.checked)} /> Photo
            </label>
            <label className={`hkp-request-capture__chip${video ? " is-on" : ""}`}>
              <input type="checkbox" checked={video} onChange={(e) => setVideo(e.target.checked)} /> Video
            </label>
            <label className="hkp-request-capture__hours">
              Link valid
              <Input
                type="number"
                min={1}
                max={72}
                bsSize="sm"
                className="hkp-request-capture__num"
                value={hours}
                onChange={(e) => setHours(Number(e.target.value) || 8)}
              />{" "}
              hours
            </label>
          </div>
          <div className="hkp-request-capture__actions">
            <Button
              type="button"
              color="primary"
              className="hkp-request-capture__generate"
              disabled={busy || !title}
              onClick={generate}
            >
              {lastUrl ? "New link" : "Create link"}
            </Button>
            <Button
              type="button"
              outline
              disabled={busy || !lastUrl}
              onClick={() => notifyCopy(lastUrl)}
            >
              Copy link
            </Button>
            <Button
              type="button"
              outline
              disabled={busy || !lastUrl}
              onClick={() => shareWhatsApp(lastUrl)}
            >
              Send on WhatsApp
            </Button>
            <Button
              type="button"
              outline
              disabled={busy}
              onClick={() => setOpenList(true)}
            >
              Existing links
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={openList}
        toggle={() => setOpenList(false)}
        centered
        zIndex={1060}
        className="hkp-request-capture-modal hkp-request-capture-modal--list"
        modalClassName="hkp-request-capture-modal-wrap"
        backdropClassName="hkp-request-capture-modal-backdrop"
      >
        <ModalHeader toggle={() => setOpenList(false)}>
          Existing links{title ? ` — ${title}` : ""}
        </ModalHeader>
        <ModalBody>
          <div className="hkp-request-capture__list">
            {forThisProcess.length ? (
              forThisProcess.map((row) => {
                const active = row.status === "OPEN";
                const used = Number(row.usedCount || 0);
                const max = Number(row.maxUploads || 0);
                return (
                  <div key={row.id} className="hkp-request-capture__row">
                    <div>
                      <span className={`hkp-request-capture__status ${active ? "is-on" : "is-off"}`}>
                        {statusLabel(row.status)}
                      </span>
                      {used > 0 ? ` · ${used}/${max || 4} used` : " · 0 / 4 used"}
                    </div>
                    {row.expiresAt ? (
                      <div className="text-muted">Expires {new Date(row.expiresAt).toLocaleString()}</div>
                    ) : null}
                    {active ? (
                      <Button
                        type="button"
                        size="sm"
                        color="danger"
                        outline
                        className="mt-2"
                        disabled={busy}
                        onClick={() => revoke(row.id)}
                      >
                        Revoke
                      </Button>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p className="text-muted small mb-0">No links generated for this process yet.</p>
            )}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default RequestCapturePanel;
